import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";

import ErrorBanner from "../../components/ErrorBanner";
import LoadingIndicator from "../../components/LoadingIndicator";
import RichTextEditor from "../../components/RichTextEditor";

import {
  useQueryGetBrokerTemplates,
  useMutateUpsertBrokerTemplate,
} from "../../data/services/brokerCommsService";
import { useQueryGetCarrierList } from "../../data/services/carrierService";

const AUDIENCE_OPTIONS = [
  { text: "Broker", value: "Broker" },
  { text: "Client", value: "Client" },
];

const BLANK_TEMPLATE = {
  brokerTemplateId: null,
  name: "",
  subject: "",
  htmlBody: "",
  version: null,
  audience: "Broker",
  carrierId: null,
};

const BrokerCommsTemplateEditor = () => {
  const [filterAudience, setFilterAudience] = React.useState("Broker");
  const [filterCarrierId, setFilterCarrierId] = React.useState(null);

  const [form, setForm] = React.useState({ ...BLANK_TEMPLATE });
  const [validationErrors, setValidationErrors] = React.useState({});
  const [successMessage, setSuccessMessage] = React.useState("");

  const {
    isLoading: templatesLoading,
    data: templatesData,
    refetch: refetchTemplates,
  } = useQueryGetBrokerTemplates(filterAudience, filterCarrierId);

  const { isLoading: carriersLoading, data: carrierListData } =
    useQueryGetCarrierList();

  const upsertMutation = useMutateUpsertBrokerTemplate();

  const carriers = React.useMemo(() => {
    if (!carrierListData?.data) return [];
    return carrierListData.data;
  }, [carrierListData]);

  const carrierOptions = React.useMemo(() => {
    return [
      { text: "Default (no carrier)", value: null },
      ...carriers.map((c) => ({ text: c.carrierName, value: c.carrierId })),
    ];
  }, [carriers]);

  const templates = React.useMemo(() => {
    if (!templatesData?.data) return [];
    return templatesData.data;
  }, [templatesData]);

  const selectedFilterCarrier = React.useMemo(
    () =>
      carrierOptions.find((c) => c.value === filterCarrierId) ||
      carrierOptions[0],
    [carrierOptions, filterCarrierId]
  );

  const selectedAudienceOption = React.useMemo(
    () =>
      AUDIENCE_OPTIONS.find((a) => a.value === form.audience) ||
      AUDIENCE_OPTIONS[0],
    [form.audience]
  );

  const selectedCarrierOption = React.useMemo(
    () =>
      carrierOptions.find((c) => c.value === form.carrierId) ||
      carrierOptions[0],
    [carrierOptions, form.carrierId]
  );

  const loadTemplate = (template) => {
    setForm({
      brokerTemplateId: template.brokerTemplateId,
      name: template.name || "",
      subject: template.subject || "",
      htmlBody: template.htmlBody || "",
      version: template.version,
      audience: template.audience || "Broker",
      carrierId: template.carrierId ?? null,
    });
    setValidationErrors({});
    setSuccessMessage("");
  };

  const handleNew = () => {
    setForm({ ...BLANK_TEMPLATE });
    setValidationErrors({});
    setSuccessMessage("");
  };

  const validate = () => {
    const errors = {};
    if (!form.name || form.name.trim() === "") {
      errors.name = "Name is required.";
    }
    if (!form.subject || form.subject.trim() === "") {
      errors.subject = "Subject is required.";
    }
    if (!form.htmlBody || form.htmlBody.trim() === "") {
      errors.htmlBody = "Body is required.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSuccessMessage("");

    const payload = {
      brokerTemplateId: form.brokerTemplateId,
      name: form.name.trim(),
      subject: form.subject.trim(),
      htmlBody: form.htmlBody,
      audience: form.audience,
      carrierId: form.carrierId,
    };

    await upsertMutation.mutateAsync(payload);
    setSuccessMessage("Template saved successfully.");
    await refetchTemplates();
  };

  if (carriersLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div style={{ display: "flex", height: "100%", gap: 0 }}>
      {/* Left panel - template list */}
      <div
        style={{
          width: 280,
          minWidth: 240,
          borderRight: "1px solid #ddd",
          display: "flex",
          flexDirection: "column",
          padding: "16px 12px",
          background: "#f9f9f9",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Templates</h3>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Audience
          </label>
          <DropDownList
            data={AUDIENCE_OPTIONS}
            textField="text"
            dataItemKey="value"
            value={
              AUDIENCE_OPTIONS.find((a) => a.value === filterAudience) ||
              AUDIENCE_OPTIONS[0]
            }
            onChange={(e) => setFilterAudience(e.target.value.value)}
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Carrier filter
          </label>
          <DropDownList
            data={carrierOptions}
            textField="text"
            dataItemKey="value"
            value={selectedFilterCarrier}
            onChange={(e) => setFilterCarrierId(e.target.value.value)}
            style={{ width: "100%" }}
          />
        </div>

        <Button
          themeColor="base"
          style={{ marginBottom: 12 }}
          onClick={handleNew}
        >
          + New Template
        </Button>

        {templatesLoading ? (
          <LoadingIndicator />
        ) : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              overflowY: "auto",
              flex: 1,
            }}
          >
            {templates.length === 0 && (
              <li style={{ color: "#888", fontSize: 13 }}>
                No templates found.
              </li>
            )}
            {templates.map((t) => (
              <li
                key={t.brokerTemplateId}
                onClick={() => loadTemplate(t)}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  borderRadius: 4,
                  marginBottom: 4,
                  background:
                    form.brokerTemplateId === t.brokerTemplateId
                      ? "#dce8f7"
                      : "#fff",
                  border:
                    form.brokerTemplateId === t.brokerTemplateId
                      ? "1px solid #0072c6"
                      : "1px solid #e0e0e0",
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                <div style={{ color: "#666", fontSize: 11 }}>
                  v{t.version} &middot; {t.audience}
                  {t.carrierId ? ` · Carrier ${t.carrierId}` : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right panel - editor */}
      <div style={{ flex: 1, padding: "16px 24px", overflowY: "auto" }}>
        <h2 style={{ marginTop: 0 }}>Broker Comms Templates</h2>

        <ErrorBanner handler={upsertMutation} />

        {successMessage && (
          <div
            style={{
              background: "#d4edda",
              color: "#155724",
              border: "1px solid #c3e6cb",
              borderRadius: 4,
              padding: "8px 14px",
              marginBottom: 12,
            }}
          >
            {successMessage}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Name <span style={{ color: "red" }}>*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              style={{ width: "100%" }}
              valid={!validationErrors.name}
            />
            {validationErrors.name && (
              <div style={{ color: "red", fontSize: 12 }}>
                {validationErrors.name}
              </div>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Subject <span style={{ color: "red" }}>*</span>
            </label>
            <Input
              value={form.subject}
              onChange={(e) =>
                setForm((f) => ({ ...f, subject: e.target.value }))
              }
              style={{ width: "100%" }}
              valid={!validationErrors.subject}
            />
            {validationErrors.subject && (
              <div style={{ color: "red", fontSize: 12 }}>
                {validationErrors.subject}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div style={{ minWidth: 160 }}>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Audience
            </label>
            <DropDownList
              data={AUDIENCE_OPTIONS}
              textField="text"
              dataItemKey="value"
              value={selectedAudienceOption}
              onChange={(e) =>
                setForm((f) => ({ ...f, audience: e.target.value.value }))
              }
              style={{ width: "160px" }}
            />
          </div>

          <div style={{ minWidth: 200 }}>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Carrier
            </label>
            <DropDownList
              data={carrierOptions}
              textField="text"
              dataItemKey="value"
              value={selectedCarrierOption}
              onChange={(e) =>
                setForm((f) => ({ ...f, carrierId: e.target.value.value }))
              }
              style={{ width: "220px" }}
            />
          </div>

          {form.version != null && (
            <div
              style={{
                minWidth: 80,
                alignSelf: "flex-end",
                paddingBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: "#555",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: "4px 10px",
                  background: "#f4f4f4",
                }}
              >
                Version {form.version}
              </span>
            </div>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Body <span style={{ color: "red" }}>*</span>
          </label>
          <RichTextEditor
            value={form.htmlBody}
            onChange={(html) => setForm((f) => ({ ...f, htmlBody: html }))}
            minHeight={320}
            ariaLabel="Template body"
            testId="html-editor"
          />
          {validationErrors.htmlBody && (
            <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
              {validationErrors.htmlBody}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <Button
            themeColor="primary"
            onClick={handleSave}
            disabled={upsertMutation.isLoading}
          >
            {upsertMutation.isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BrokerCommsTemplateEditor;
